import {
    DropdownMenuProvider,
    useDropdownMenuContext,
    useDropdownRootContext,
} from '../DropdownContext/dropdownContext'
import React, {
    useEffect,
    useRef,
    useState,
} from 'react'

import {styled} from 'stitches/config'
import {styledDropdownContent} from './styles'


type Child = React.ReactElement<HTMLButtonElement> | false;

interface DropdownContentProps {
    children: Child | Child[];
    customClass?: 'full-width' | 'unstyled' | 'with-submenu' | null;
    direction?: 'left' | 'right' | 'top-left' | 'top-right' | 'fixed-end' | 'fixed-right'; // fixed-right and fixed-end are the one to use inside Table component
}


const StyledDropdownContent = styled('div', styledDropdownContent)

const DropdownContentView: React.FC<DropdownContentProps> = (props) => {
    const {
        children,
        customClass,
        direction,
    } = props

    const {
        isOpen = false, onToggle,
    } = useDropdownMenuContext()

    const {
        closeAfterSelection = true, dropdownRef,
    } = useDropdownRootContext()

    const ref = React.useRef<HTMLDivElement>(null)

    const [offsetStyle, setOffsetStyle] = useState<React.CSSProperties>({})
    const fixedRightPosition = useRef<'top' | 'bottom' | null>(null)
    const observerRef = useRef<IntersectionObserver | null>(null)

    const onClick = React.useCallback((): void => {
        if (closeAfterSelection) {
            onToggle?.()
        }
    }, [closeAfterSelection])

    const intersectionCallback = (entry: IntersectionObserverEntry) => {
        if (!entry.isIntersecting) {
            return
        }

        const dir = entry.boundingClientRect.x < entry.intersectionRect.x ? 'left' : 'right'
        const offset = entry.boundingClientRect.width - entry.intersectionRect.width

        switch (direction) {
            case 'fixed-end':
            case 'fixed-right':
                break
            case 'right':
            case 'top-right':
                setOffsetStyle({right: `${dir === 'left' ? '-' : ''}${offset}px`})
                break
            case 'left':
            case 'top-left':
            default:
                setOffsetStyle({left: `${dir === 'left' ? '' : '-'}${offset}px`})
        }
    }

    // this functions is needed to find ancestor with overflowY
    // to be able to attach scroll addEventListener for calculating dropdown position
    const findScrollContainer = (element: Element) => {
        if (!element) {
            return undefined
        }

        let parent = element.parentElement
        while (parent) {
            const {overflowY} = window.getComputedStyle(parent)
            if (overflowY.split(' ').every((o) => o === 'auto' || o === 'scroll')) {
                return parent
            }
            parent = parent.parentElement
        }

        return document.documentElement
    }

    const calculateoffsetStyle = (isResize = false) => {
        const isFixedDirection = direction === 'fixed-end' || direction === 'fixed-right'
        if (!isFixedDirection || !dropdownRef?.current || !ref?.current) {
            return
        }

        const dropdown = dropdownRef.current.getBoundingClientRect()
        const sidebarContent = dropdownRef.current.closest('[data-id="sidebar-content"]') // for sidebar dropdowns
        const boundaryRect = (sidebarContent ?? document.documentElement).getBoundingClientRect()
        const padding = 8
        const anchorRight = Math.min(dropdown.right, boundaryRect.right - padding)
        const availableWidth = Math.max(0, anchorRight - boundaryRect.left - padding)
        const menuHeight = ref.current.offsetHeight
        const left = Math.max(
            boundaryRect.left + padding,
            anchorRight - Math.min(ref.current.offsetWidth, availableWidth || ref.current.offsetWidth),
        )
        const position = isResize && fixedRightPosition.current
            ? fixedRightPosition.current
            : dropdown.bottom + menuHeight > boundaryRect.bottom ? 'top' : 'bottom'
        const top = position === 'top'
            ? dropdown.top - menuHeight - 10
            : dropdown.bottom
        const isOutsideBounds = top < boundaryRect.top || top + menuHeight > boundaryRect.bottom

        fixedRightPosition.current = position

        setOffsetStyle({
            left,
            maxWidth: Math.min(300, availableWidth) ?? 300,
            minWidth: Math.min(220, availableWidth) ?? 220,
            position: 'fixed',
            top,
            visibility: isOutsideBounds ? 'hidden' : 'visible',
        })
    }

    const resizeScrollHandler = () => {
        calculateoffsetStyle(true)
    }

    useEffect(() => {
        if (!isOpen) {
            return undefined
        }

        if ((direction === 'fixed-end' || direction === 'fixed-right') && dropdownRef?.current && ref?.current) {
            calculateoffsetStyle()

            const tdParent = dropdownRef.current.closest('td')
            if (tdParent) {
                // when opening dropdown need to add BIG z-index to parent table cell
                // to make sure it will not be covered by other table cells
                // this is needed when table cell use position: sticky on smaller screens
                tdParent.style.zIndex = '100'
            }

            const scrollableParentInsideTable = findScrollContainer(dropdownRef?.current)
            scrollableParentInsideTable?.addEventListener('scroll', resizeScrollHandler)

            const parentTable = dropdownRef?.current.closest('[data-id="table-wrapper"]')
            if (parentTable) {
                const scrollableParentOutsideTable = findScrollContainer(parentTable)
                scrollableParentOutsideTable?.addEventListener('scroll', resizeScrollHandler)
            }

            window.addEventListener('resize', resizeScrollHandler)
        }

        observerRef.current?.disconnect()
        observerRef.current = new IntersectionObserver(([entry]) => intersectionCallback(entry), {threshold: 0})

        if (observerRef.current && ref.current) {
            observerRef.current.observe(ref.current)
        }

        return () => {
            observerRef.current?.disconnect()
            observerRef.current = null

            // table dropdown cleanup
            if ((direction === 'fixed-end' || direction === 'fixed-right') && dropdownRef?.current) {
                window.removeEventListener('resize', resizeScrollHandler)

                const scrollableParentInsideTable = findScrollContainer(dropdownRef?.current)
                scrollableParentInsideTable?.removeEventListener('scroll', resizeScrollHandler)

                const parentTable = dropdownRef?.current.closest('[data-id="table-wrapper"]')
                if (parentTable) {
                    const scrollableParentOutsideTable = findScrollContainer(parentTable)
                    scrollableParentOutsideTable?.removeEventListener('scroll', resizeScrollHandler)
                }

                const tdParent = dropdownRef?.current?.closest('td')
                if (tdParent) {
                    tdParent.style.zIndex = ''
                }
            }
        }
    }, [isOpen])

    if (!isOpen) {
        return null
    }

    return (
        <DropdownMenuProvider value={{
            isOpen,
            onToggle: onClick,
        }}
        >
            <StyledDropdownContent
                direction={direction}
                flavor={customClass || 'default'}
                onClick={onClick}
                open={isOpen}
                ref={ref}
                style={offsetStyle}
            >
                {children}
            </StyledDropdownContent>
        </DropdownMenuProvider>
    )
}

DropdownContentView.defaultProps = {
    customClass: null,
    direction: 'left',
}

export default DropdownContentView
